import { TreeDataItem as BaseTreeDataItem } from "@/components/tree-view";
import { EmailFolder } from "@cohesive/api-entities";
import {
  Archive,
  ArchiveX,
  Folder,
  Inbox,
  LucideProps,
  Send,
  SquarePen,
  Trash,
} from "lucide-react";
import React from "react";

// Extend the TreeDataItem interface to include childFolderCount
// Use type alias instead of interface extension to avoid type conflicts
export type TreeDataItem = BaseTreeDataItem & {
  labelType?: string;
};

// Define the type for TreeSVGIcon to match the TreeView component
export type TreeSVGIcon = React.ForwardRefExoticComponent<
  Omit<LucideProps, "ref"> & React.RefAttributes<SVGSVGElement>
>;

export type MessageTabConfig = {
  type: "message";
  folderId: string;
  label: string;
  icon: React.ReactNode;
};

export type TabConfig = MessageTabConfig;

export const DEFAULT_FOLDERS_ORDER = [
  "Inbox",
  "Drafts",
  "Sent Items",
  "Deleted Items",
  "Archive",
  "Junk Email",
  "Notes",
];

export const getMailFolderIcon = (
  folder: string,
): React.ForwardRefExoticComponent<
  Omit<LucideProps, "ref"> & React.RefAttributes<SVGSVGElement>
> => {
  switch (folder) {
    case "Archive":
      return Archive;
    case "Conversation History":
      return Folder;
    case "Deleted Items":
      return Trash;
    case "Drafts":
      return SquarePen;
    case "Inbox":
      return Inbox;
    case "Junk Email":
      return ArchiveX;
    case "Outbox":
      return Send;
    case "Sent Items":
      return Send;
    default:
      return Folder;
  }
};

// Function to parse nested label paths into a tree structure
export interface LabelTreeNode {
  id: string;
  name: string;
  fullPath: string;
  type: string;
  children: LabelTreeNode[];
  parent?: LabelTreeNode;
  messagesTotal: number;
}

export function buildLabelTree(labels: EmailFolder[]): LabelTreeNode[] {
  const rootNodes: LabelTreeNode[] = [];
  const nodeMap = new Map<string, LabelTreeNode>();

  // First pass: create all nodes
  labels.forEach((label) => {
    const node: LabelTreeNode = {
      id: label.id,
      name: label.name,
      fullPath: label.name,
      type: label.type,
      children: [],
      messagesTotal: label.total_items,
    };

    nodeMap.set(label.id, node);
  });

  // Second pass: build the tree structure
  labels.forEach((label) => {
    const node = nodeMap.get(label.id);
    if (!node) return;

    if (!label.parent_id) {
      // This is a root node
      rootNodes.push(node);
    } else {
      // This has a parent
      const parentNode = nodeMap.get(label.parent_id);

      if (parentNode) {
        parentNode.children.push(node);
        node.parent = parentNode;
      } else {
        // Parent referenced but not found in our data, add to root
        rootNodes.push(node);
      }
    }
  });

  return rootNodes;
}
